/* Veyron - comportamento das secoes globais. Vanilla JS, sem dependencias. */
(function () {
  'use strict';

  const focusablesSel = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    function onKey(e) {
      if (e.key === 'Escape') { onEscape(); return; }
      if (e.key !== 'Tab') return;
      const items = Array.from(container.querySelectorAll(focusablesSel)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  }

  /* ---------------- Countdown ---------------- */
  class VeyCountdown extends HTMLElement {
    connectedCallback() {
      this.digits = {
        days: this.querySelector('[data-unit="days"] .vey-countdown__digits'),
        hours: this.querySelector('[data-unit="hours"] .vey-countdown__digits'),
        minutes: this.querySelector('[data-unit="minutes"] .vey-countdown__digits'),
        seconds: this.querySelector('[data-unit="seconds"] .vey-countdown__digits')
      };
      this.mode = this.dataset.mode || 'daily';
      this.deadline = this.computeDeadline();
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }

    computeDeadline() {
      if (this.mode === 'fixed' && this.dataset.endsAt) {
        const parsed = new Date(this.dataset.endsAt.replace(' ', 'T')).getTime();
        if (!isNaN(parsed)) return parsed;
      }
      const parts = (this.dataset.resetAt || '23:59').split(':');
      const d = new Date();
      d.setHours(Number(parts[0]) || 23, Number(parts[1]) || 59, 0, 0);
      if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
      return d.getTime();
    }

    tick() {
      let diff = this.deadline - Date.now();
      if (diff <= 0) {
        if (this.mode === 'daily') { this.deadline = this.computeDeadline(); diff = this.deadline - Date.now(); }
        else { diff = 0; clearInterval(this.timer); }
      }
      const s = Math.floor(diff / 1000);
      this.set('days', Math.floor(s / 86400));
      this.set('hours', Math.floor((s % 86400) / 3600));
      this.set('minutes', Math.floor((s % 3600) / 60));
      this.set('seconds', s % 60);
    }
    set(unit, val) {
      if (this.digits[unit]) this.digits[unit].textContent = String(val).padStart(2, '0');
    }
  }
  customElements.define('vey-countdown', VeyCountdown);

  /* ---------------- Header + menu drawer ---------------- */
  class VeyHeader extends HTMLElement {
    connectedCallback() {
      this.drawer = document.querySelector('.vey-drawer[data-section-id="' + this.dataset.sectionId + '"]');
      this.burger = this.querySelector('.vey-header__burger');
      if (!this.drawer || !this.burger) return;
      this.burger.addEventListener('click', () => this.open());
      this.drawer.querySelectorAll('[data-drawer-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
    }
    open() {
      this.drawer.dataset.open = 'true';
      document.body.classList.add('vey-scroll-lock');
      this.burger.setAttribute('aria-expanded', 'true');
      this.untrap = trapFocus(this.drawer, () => this.close());
      const first = this.drawer.querySelector(focusablesSel);
      if (first) first.focus();
    }
    close() {
      this.drawer.dataset.open = 'false';
      document.body.classList.remove('vey-scroll-lock');
      this.burger.setAttribute('aria-expanded', 'false');
      if (this.untrap) this.untrap();
      this.burger.focus();
    }
  }
  customElements.define('vey-header', VeyHeader);

  /* ---------------- Cart drawer ---------------- */
  class VeyCartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); }
        const minus = e.target.closest('[data-qty-minus]');
        const plus = e.target.closest('[data-qty-plus]');
        const remove = e.target.closest('[data-cart-remove]');
        if (minus || plus) { e.preventDefault(); this.step(minus || plus, minus ? -1 : 1); }
        if (remove) { e.preventDefault(); this.change(remove.dataset.line, 0); }
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-qty-input]');
        if (input) this.change(input.dataset.line, parseInt(input.value, 10));
      });

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-open], a[href$="/cart"]');
        if (!trigger) return;
        if (trigger.hasAttribute('data-cart-no-drawer')) return;
        e.preventDefault();
        this.open();
      });

      document.addEventListener('vey:cart:refresh', () => this.refresh());
      this.interceptAddToCart();
    }

    open() {
      this.dataset.open = 'true';
      document.body.classList.add('vey-scroll-lock');
      this.untrap = trapFocus(this, () => this.close());
      const f = this.querySelector(focusablesSel);
      if (f) f.focus();
    }
    close() {
      this.dataset.open = 'false';
      document.body.classList.remove('vey-scroll-lock');
      if (this.untrap) this.untrap();
    }

    setBusy(state) { this.classList.toggle('vey-cart--busy', state); }

    async change(line, quantity) {
      this.setBusy(true);
      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity: quantity })
        });
        if (res.ok) await this.refresh();
      } finally { this.setBusy(false); }
    }

    step(btn, delta) {
      const wrap = btn.closest('.vey-cart__qty');
      const input = wrap && wrap.querySelector('[data-qty-input]');
      if (!input) return;
      const next = Math.max(0, parseInt(input.value, 10) + delta);
      input.value = next;
      this.change(input.dataset.line, next);
    }

    async refresh() {
      const url = window.Shopify.routes.root + '?sections=' + this.sectionId;
      const res = await fetch(url);
      const data = await res.json();
      const html = data[this.sectionId];
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const newInner = fresh.querySelector('vey-cart-drawer');
      const wasOpen = this.dataset.open === 'true';
      if (newInner) this.innerHTML = newInner.innerHTML;
      if (wasOpen) this.dataset.open = 'true';
      this.updateCounts();
    }

    async updateCounts() {
      const res = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    }

    interceptAddToCart() {
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('form[action$="/cart/add"]');
        if (!form || form.hasAttribute('data-no-drawer')) return;
        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        if (btn) btn.setAttribute('aria-busy', 'true');
        try {
          const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' }
          });
          if (res.ok) { await this.refresh(); this.open(); }
          else form.submit();
        } catch (err) { form.submit(); }
        finally { if (btn) btn.removeAttribute('aria-busy'); }
      });
    }
  }
  customElements.define('vey-cart-drawer', VeyCartDrawer);

  /* ---------------- Trust columns ---------------- */
  class VeyTrustColumns extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.vey-trust__track');
      this.dots = Array.from(this.querySelectorAll('.vey-trust__dot'));
      if (!this.track || !this.dots.length) return;
      this.dots.forEach((dot, i) =>
        dot.addEventListener('click', () => {
          const item = this.track.children[i];
          if (item) this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
        })
      );
      this.track.addEventListener('scroll', () => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.dots.forEach((d, idx) => d.setAttribute('aria-selected', String(idx === i)));
      }, { passive: true });
    }
  }
  customElements.define('vey-trust-columns', VeyTrustColumns);

  /* ---------------- Footer accordion ---------------- */
  class VeyFooterAccordion extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('.vey-footer__col--collapsible').forEach((col) => {
        const btn = col.querySelector('.vey-footer__col-title');
        if (!btn || btn.tagName !== 'BUTTON') return;
        btn.addEventListener('click', () => {
          const open = col.dataset.open === 'true';
          col.dataset.open = String(!open);
          btn.setAttribute('aria-expanded', String(!open));
        });
      });
    }
  }
  customElements.define('vey-footer-accordion', VeyFooterAccordion);

  /* ---------------- App bar ---------------- */
  class VeyAppBar extends HTMLElement {
    connectedCallback() {
      this.key = 'vey-appbar-dismissed-' + this.dataset.sectionId;
      try { if (localStorage.getItem(this.key) === '1') { this.hidden = true; return; } } catch (err) {}
      const close = this.querySelector('.vey-app-bar__close');
      if (close) close.addEventListener('click', () => {
        this.hidden = true;
        try { localStorage.setItem(this.key, '1'); } catch (err) {}
      });
    }
  }
  customElements.define('vey-app-bar', VeyAppBar);
})();
